import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';

import { segmentTrack } from '@/hooks/analytics/useTracking';
import { useQueryParam } from '@/hooks/useQueryParam';
import { TriggerTypes } from '@/models/Build';
import BuildStatus from '@/models/BuildStatus';
import { PipelineStatus } from '@/models/Pipeline';
import BuildListFilters from '@/pages/App/AppDetails/types/BuildListFilters';
import { mergeQueryParams } from '@/utils/queryParams';

export type BuildFilterName =
  | 'after_date'
  | 'artifact_type'
  | 'before_date'
  | 'branch'
  | 'pipeline'
  | 'search'
  | 'status'
  | 'trigger_event_type'
  | 'workflow';

export type BuildFilterValues = {
  artifactType: string[];
  branch: string[];
  dates: [Date, Date] | null;
  search: string;
  status: string[];
  triggerEventType: string[];
  /** `pipeline-<name>` or `workflow-<name>`. */
  workflow: string[];
};

export const emptyBuildFilters: BuildFilterValues = {
  artifactType: [],
  branch: [],
  dates: null,
  search: '',
  status: [],
  triggerEventType: [],
  workflow: [],
};

const useBuildFilterState = (
  appSlug: string,
  updateFilters: (filters: BuildListFilters) => void,
): {
  applyFilters: (patch: Partial<BuildFilterValues>) => void;
  values: BuildFilterValues;
} => {
  const afterDateFilter = useQueryParam('after_date', undefined) || null;
  const artifactFilter = useQueryParam('artifact_type', undefined) || null;
  const beforeDateFilter = useQueryParam('before_date', undefined) || null;
  const branchFilter = useQueryParam('branch', undefined) || null;
  const pipelineFilter = useQueryParam('pipeline', undefined) || null;
  const searchFilter = useQueryParam('search', '');
  const statusFilter = useQueryParam('status', undefined) || null;
  const triggerFilter = useQueryParam('trigger_event_type', undefined) || null;
  const workflowFilter = useQueryParam('workflow', undefined) || null;

  useEffect(() => {
    updateFilters({
      afterDate: afterDateFilter ? DateTime.fromMillis(Number(afterDateFilter)).toISO() : null,
      artifactType: artifactFilter,
      beforeDate: beforeDateFilter ? DateTime.fromMillis(Number(beforeDateFilter)).toISO() : null,
      branch: branchFilter,
      pipelineable: workflowFilter || pipelineFilter,
      status: statusFilter as BuildStatus | PipelineStatus | null,
      term: searchFilter,
      triggerType: triggerFilter as TriggerTypes | null,
    });
  }, [
    afterDateFilter,
    artifactFilter,
    beforeDateFilter,
    branchFilter,
    pipelineFilter,
    searchFilter,
    statusFilter,
    triggerFilter,
    updateFilters,
    workflowFilter,
  ]);

  const values = useMemo<BuildFilterValues>(
    () => ({
      artifactType: artifactFilter ? [artifactFilter] : [],
      branch: branchFilter ? [branchFilter] : [],
      dates:
        afterDateFilter && beforeDateFilter
          ? [new Date(Number(afterDateFilter)), new Date(Number(beforeDateFilter))]
          : null,
      search: searchFilter,
      status: statusFilter ? [statusFilter] : [],
      triggerEventType: triggerFilter ? [triggerFilter] : [],
      // The older `pipeline` parameter still filters; the next change rewrites it as `workflow`.
      workflow: workflowFilter || pipelineFilter ? [(workflowFilter || pipelineFilter) as string] : [],
    }),
    [
      afterDateFilter,
      artifactFilter,
      beforeDateFilter,
      branchFilter,
      pipelineFilter,
      searchFilter,
      statusFilter,
      triggerFilter,
      workflowFilter,
    ],
  );

  // The latest values, for a late call such as the search's debounce, which mustn't undo a change made since.
  const latestValues = useRef(values);
  useEffect(() => {
    latestValues.current = values;
  }, [values]);

  // Writes the whole filter set to the URL in one go, and tracks it.
  const applyFilters = (patch: Partial<BuildFilterValues>) => {
    const next = { ...latestValues.current, ...patch };
    // Right away, so another callback before the URL-driven rerender builds on this change, not before it.
    latestValues.current = next;
    const filters: { key: BuildFilterName; val: string }[] = [
      { key: 'artifact_type', val: next.artifactType[0] ?? '' },
      { key: 'branch', val: next.branch[0] ?? '' },
      { key: 'pipeline', val: '' },
      { key: 'search', val: next.search },
      { key: 'status', val: next.status[0] ?? '' },
      { key: 'trigger_event_type', val: next.triggerEventType[0] ?? '' },
      { key: 'workflow', val: next.workflow[0] ?? '' },
      { key: 'after_date', val: next.dates ? String(next.dates[0].getTime()) : '' },
      { key: 'before_date', val: next.dates ? String(next.dates[1].getTime()) : '' },
    ];

    const tracked = filters.filter(({ key, val }) => val && !['after_date', 'before_date'].includes(key));
    if (tracked.length || next.dates) {
      segmentTrack('Build List Filter Changed', {
        filter_options: tracked.map(({ key }) => key).join(', '),
        filter_values: tracked.map(({ val }) => val).join(', '),
        filter_date_period_start: next.dates ? DateTime.fromJSDate(next.dates[0]).toISO() : undefined,
        filter_date_period_end: next.dates ? DateTime.fromJSDate(next.dates[1]).toISO() : undefined,
        app_slug: appSlug,
      });
    }

    mergeQueryParams({
      set: filters.filter(({ val }) => Boolean(val)).map(({ key, val }) => ({ type: key, value: val })),
      unset: filters.filter(({ val }) => !val).map(({ key }) => key),
    });
  };

  return { applyFilters, values };
};

export default useBuildFilterState;
